import { extendObservable, action } from 'mobx';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import copyText from '../../copyText';
import { apiCall } from '../../API';
import filterOutDeletedPolicyCategories from '../filterOutDeletedPolicyCategories';
import AsyncActionStore from '../AsyncActionStore';
import MenuStore from '../MenuStore';
import BrowserPolicyEditStore from './BrowserPolicyEditStore';
import makeDefaultBrowserPolicy from './makeDefaultBrowserPolicy';
import AccessCodesEditStore from './AccessCodesEditStore';

export default class BrowserPoliciesStore {
    static PropType = PropTypes.shape({
        listStatus: AsyncActionStore.PropType.isRequired,
        policies: MobxPropTypes.arrayOrObservableArray,
        defaultPolicyId: PropTypes.number,
        createStatus: AsyncActionStore.PropType.isRequired,
        updateStatus: AsyncActionStore.PropType.isRequired,
        menu: MenuStore.PropType.isRequired,
        editStore: BrowserPolicyEditStore.PropType,
        load: PropTypes.func.isRequired,
        showUpdate: PropTypes.func.isRequired,
        showView: PropTypes.func.isRequired,
        showCreate: PropTypes.func.isRequired,
        createPolicy: PropTypes.func.isRequired,
        updatePolicy: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        extendObservable(this, /** @class BrowserPoliciesStore */{
            // list of policies
            apiList: apiCall('/console/browser_policy/list'),
            listStatus: new AsyncActionStore(),
            policies: [],
            defaultPolicyId: null,
            menu: new MenuStore(),
            policyExpandedRow: null,

            // editing one policy
            editStore: new BrowserPolicyEditStore(),
            apiCreate: apiCall('/console/browser_policy/create'),
            createStatus: new AsyncActionStore(),
            apiUpdate: apiCall('/console/browser_policy/update'),
            updateStatus: new AsyncActionStore(),
            apiDelete: apiCall('/console/browser_policy/delete'),
            deleteStatus: new AsyncActionStore(),
            defaultForbiddenCategories: [],

            // access codes
            accessCodesEditStore: new AccessCodesEditStore(),
        }, data);
    }

    makePlainPolicyObject = (customerPolicy) => ({
        ...makeDefaultBrowserPolicy(customerPolicy.id ? [] : [...this.defaultForbiddenCategories.values()]),
        ...customerPolicy.policy,
        id: customerPolicy.id,
        totalActivations: customerPolicy.totalActivations,
        accessCodes: customerPolicy.accessCodes || [],
        activationsAllowed: !customerPolicy.accessCodes ? 0 :
            customerPolicy.accessCodes.reduce((total, accessCode) => total + (accessCode.activationsAllowed || 0), 0),
        isDefault: this.defaultPolicyId && customerPolicy.id === this.defaultPolicyId,
    });

    load = action(() => {
        if (!this.listStatus.isLoading) {
            this.listStatus.setLoading(this.apiList())
                .then(({ policies, defaultForbiddenCategories, categories, defaultPolicyId }) => {
                    this.defaultPolicyId = defaultPolicyId;
                    this.defaultForbiddenCategories.replace(defaultForbiddenCategories);
                    this.editStore.setMetadata(categories);

                    filterOutDeletedPolicyCategories(policies, categories);

                    policies = policies.reverse();
                    const defaultPolicyIndex = policies.findIndex((policy) => policy.id === defaultPolicyId);
                    if (defaultPolicyIndex >= 0) {
                        const defaultPolicy = policies.splice(defaultPolicyIndex, 1)[0];
                        policies.push(defaultPolicy);
                    }
                    this.policies.replace(policies.map(this.makePlainPolicyObject));
                });
        }
        return this.listStatus.promise;
    });

    showUpdate = action((policy) => {
        this.menu.close();
        this.editStore.setValue(policy);
        this.editStore.viewMode = false;
        this.editStore.modal.open();
    });

    showView = action((policy) => {
        this.menu.close();
        this.editStore.setValue(policy);
        this.editStore.viewMode = true;
        this.editStore.modal.open();
    });

    showCreate = action(() => {
        this.editStore.setValue(this.makePlainPolicyObject({ id: null }));
        this.editStore.viewMode = false;
        this.editStore.modal.open();
    });

    createPolicy = action(() => {
        if (!this.createStatus.isLoading) {
            const policyData = this.editStore.value();
            policyData.type = 'BrowserIsolationPolicy';

            if (this.editStore.nameStore.isEmpty) {
                this.editStore.nameStore.setTouched(false);
                this.editStore.nameStore.focus();
                return Promise.reject();
            }

            this.createStatus.setLoading(this.apiCreate(policyData)
                .then(action((customerPolicy) => {
                    const newPolicy = this.makePlainPolicyObject(customerPolicy);
                    this.policies.replace([newPolicy, ...this.policies]);
                    this.editStore.modal.close();
                    this.togglePolicyExpand(newPolicy);
                    return newPolicy;
                })));
        }
        return this.createStatus.promise;
    });


    updatePolicy = action(() => {
        if (!this.updateStatus.isLoading) {
            const policy = this.editStore.value();
            policy.type = 'BrowserIsolationPolicy';

            if (this.editStore.nameStore.isEmpty) {
                this.editStore.nameStore.setTouched(false);
                this.editStore.nameStore.focus();
                return Promise.reject();
            }

            this.updateStatus.setLoading(this.apiUpdate({ id: policy.id, policy }))
                .then(action((newPolicy) => {
                    const finalPolicy = this.makePlainPolicyObject(newPolicy);
                    this.policies.splice(this.policies.findIndex((p) => p.id === policy.id), 1, finalPolicy);
                    this.policies.replace([...this.policies]);
                    this.editStore.modal.close();
                    return newPolicy;
                }));
        }
        return this.updateStatus.promise;
    });

    showDelete = action((policy) => {
        this.menu.close();
        this.editStore.setValue(policy);
        this.editStore.deleteModal.open();
    });

    deletePolicy = action(() => {
        if (!this.deleteStatus.isLoading) {
            this.editStore.deleteConfirmation.setFalse();
            const policy = this.editStore.value();
            this.deleteStatus.setLoading(this.apiDelete({ id: policy.id, policy: null }))
                .then(action((response) => {
                    if (response) {
                        this.policies.splice(this.policies.findIndex((p) => p.id === policy.id), 1);
                        this.policies.replace([...this.policies]);
                        this.editStore.deleteModal.close();
                    }
                    return policy;
                }))
                .catch((error) => {
                    this.editStore.deleteModal.close();
                    return error;
                });
        }
        return this.deleteStatus.promise;
    });

    saveAccessCode = action((accessCode) => {
        const policy = this.policies.find((p) => p.id === accessCode.policyId);
        if (policy) {
            policy.accessCodes.push(accessCode);
        }
        this.accessCodesEditStore.closeModal();
    });

    updateAccessCode = action((accessCode) => {
        const policy = this.policies.find((p) => p.id === accessCode.policyId);
        if (policy) {
            policy.accessCodes
                .splice(policy.accessCodes.findIndex((a) => a.id === accessCode.id), 1, accessCode);
            policy.accessCodes.replace([...policy.accessCodes]);
        }
        this.accessCodesEditStore.closeModal();
    });

    removeAccessCode = action((accessCode) => {
        const policy = this.policies.find((p) => p.id === accessCode.policyId);
        policy.accessCodes.replace(policy.accessCodes.filter((a) => a.id !== accessCode.id));
        this.accessCodesEditStore.closeModal();
    });

    togglePolicyExpand = action((policy) => {
        this.policyExpandedRow = this.policyExpandedRow === policy.id ? null : policy.id;
    });

    copyTextToClipboard = (text) => copyText(text);
}
